import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Users, Search, Sparkles, RefreshCw, AlertCircle, User, Phone, Mail,
  Activity, ShieldAlert, Pill, Stethoscope, Clock, FileText, X
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const triageColor = (t) => {
  const v = (t || '').toUpperCase();
  if (v.includes('EMERGENCY') && !v.includes('NON')) return 'bg-red-500/20 text-red-300 border-red-500/30';
  if (v.includes('URGENT')) return 'bg-amber-500/20 text-amber-300 border-amber-500/30';
  if (v) return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30';
  return 'bg-slate-600/40 text-slate-300 border-slate-500/30';
};

const ClientProfiles = () => {
  const { user, isSuperAdmin } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('');

  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [search, setSearch] = useState('');
  const [triageFilter, setTriageFilter] = useState('all');

  const [nlQuery, setNlQuery] = useState('');
  const [nlResult, setNlResult] = useState(null);
  const [nlLoading, setNlLoading] = useState(false);

  const [loading, setLoading] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [error, setError] = useState(null);

  // Load tenants
  useEffect(() => {
    const loadTenants = async () => {
      try {
        let list = [];
        if (isSuperAdmin && isSuperAdmin()) {
          const res = await axios.get(`${API}/tenants`);
          list = res.data || [];
        } else {
          list = (user?.tenants || []).map((t) => ({
            tenant_id: t.tenant_id, name: t.name, industry_type: t.industry_type,
          }));
        }
        setTenants(list);
        if (list.length > 0) setSelectedTenant(list[0].tenant_id);
      } catch (e) {
        console.error(e);
        setError('Failed to load tenants');
      }
    };
    loadTenants();
  }, [user, isSuperAdmin]);

  const loadClients = useCallback(async (tenantId, searchTerm, triage) => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ tenant_id: tenantId });
      if (searchTerm) params.append('search', searchTerm);
      if (triage && triage !== 'all') params.append('triage', triage);
      const res = await axios.get(`${API}/clients?${params.toString()}`);
      setClients(res.data || []);
    } catch (e) {
      console.error(e);
      setError('Failed to load client profiles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedTenant) loadClients(selectedTenant, '', 'all');
  }, [selectedTenant, loadClients]);

  const handleSearch = () => loadClients(selectedTenant, search, triageFilter);

  const runNlQuery = async () => {
    if (!nlQuery.trim() || !selectedTenant) return;
    setNlLoading(true);
    setNlResult(null);
    setError(null);
    try {
      const res = await axios.post(`${API}/clients/query`, {
        tenant_id: selectedTenant,
        query: nlQuery,
      });
      setNlResult(res.data);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || 'Query failed');
    } finally {
      setNlLoading(false);
    }
  };

  const rebuildProfiles = async () => {
    if (!selectedTenant) return;
    setRebuilding(true);
    setError(null);
    try {
      await axios.post(`${API}/clients/rebuild/${selectedTenant}`);
      await loadClients(selectedTenant, search, triageFilter);
    } catch (e) {
      console.error(e);
      setError('Failed to rebuild profiles');
    } finally {
      setRebuilding(false);
    }
  };

  const nlMatchIds = nlResult ? new Set((nlResult.matches || []).map((m) => m.client_id)) : null;
  const displayClients = nlMatchIds ? clients.filter((c) => nlMatchIds.has(c.client_id)) : clients;

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-gradient-to-r from-teal-500/10 to-cyan-500/10 border border-teal-500/20">
            <Users className="w-5 h-5 text-teal-400" />
            <span className="text-white font-medium">Client Profiles</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">
            Client <span className="bg-gradient-to-r from-teal-400 to-cyan-500 bg-clip-text text-transparent">Records</span>
          </h1>
          <p className="text-sm sm:text-base text-slate-300">
            Aggregated profiles across every intake — searchable and queryable in plain English.
          </p>
        </div>

        {/* Controls */}
        <div className="flex flex-col lg:flex-row gap-3 bg-slate-800/50 border border-slate-700 rounded-xl p-4">
          <div className="w-full lg:max-w-xs space-y-1">
            <label className="text-xs text-slate-400">Tenant</label>
            <Select value={selectedTenant} onValueChange={(v) => { setSelectedTenant(v); setNlResult(null); setSelectedClient(null); }}>
              <SelectTrigger className="bg-slate-900 border-slate-600 text-white" data-testid="clients-tenant-select">
                <SelectValue placeholder="Choose tenant..." />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700 text-white">
                {tenants.map((t) => (
                  <SelectItem key={t.tenant_id} value={t.tenant_id}>{t.name} · {t.industry_type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 space-y-1">
            <label className="text-xs text-slate-400">Search (name, symptom, condition…)</label>
            <div className="flex gap-2">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="e.g. cough, asthma, Jane…"
                className="bg-slate-900 border-slate-600 text-white"
                data-testid="clients-search-input"
              />
              <Select value={triageFilter} onValueChange={setTriageFilter}>
                <SelectTrigger className="bg-slate-900 border-slate-600 text-white w-36">
                  <SelectValue placeholder="Triage" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 text-white">
                  <SelectItem value="all">All triage</SelectItem>
                  <SelectItem value="EMERGENCY">Emergency</SelectItem>
                  <SelectItem value="URGENT">Urgent</SelectItem>
                  <SelectItem value="ROUTINE">Routine</SelectItem>
                  <SelectItem value="NON_EMERGENCY">Non-emergency</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleSearch} className="bg-teal-500 hover:bg-teal-600 text-white" data-testid="clients-search-button">
                <Search className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="space-y-1 flex flex-col justify-end">
            <label className="text-xs text-slate-400 lg:opacity-0">.</label>
            <Button onClick={rebuildProfiles} disabled={rebuilding} variant="outline" className="border-slate-600 text-slate-200 hover:bg-slate-700">
              {rebuilding ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Rebuild
            </Button>
          </div>
        </div>

        {/* Natural-language query */}
        <div className="bg-gradient-to-r from-slate-800/60 to-slate-800/30 border border-teal-500/20 rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm text-slate-300">
            <Sparkles className="w-4 h-4 text-teal-400" />
            Ask about your clients in plain English
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              value={nlQuery}
              onChange={(e) => setNlQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && runNlQuery()}
              placeholder="e.g. Which clients had severe respiratory symptoms or a penicillin allergy?"
              className="bg-slate-900 border-slate-600 text-white"
              data-testid="clients-nl-input"
            />
            <Button onClick={runNlQuery} disabled={nlLoading || !nlQuery.trim()} className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white" data-testid="clients-nl-button">
              {nlLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Ask
            </Button>
          </div>
          {nlResult && (
            <Alert className="bg-teal-500/10 border-teal-500/30 text-teal-100">
              <Sparkles className="h-4 w-4" />
              <AlertDescription>
                <span className="text-teal-200">{nlResult.answer || 'No answer.'}</span>
                <span className="block text-xs text-slate-400 mt-1">
                  Searched {nlResult.total_clients_searched} client(s) · {nlResult.matches?.length || 0} match(es) shown below.
                  <button onClick={() => setNlResult(null)} className="ml-2 underline">clear filter</button>
                </span>
              </AlertDescription>
            </Alert>
          )}
        </div>
      </div>

      {error && (
        <Alert className="bg-red-500/10 border-red-500/20 text-red-400">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* List */}
        <div className="lg:col-span-1 space-y-3">
          <p className="text-sm text-slate-400">{displayClients.length} client(s)</p>
          {loading ? (
            [1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full bg-slate-700" />)
          ) : displayClients.length === 0 ? (
            <div className="text-center py-10 text-slate-400 bg-slate-800/40 rounded-lg border border-slate-700">
              <Users className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>No client profiles yet.</p>
              <p className="text-xs mt-1">Profiles are created automatically after each completed intake.</p>
            </div>
          ) : (
            displayClients.map((c) => (
              <button
                key={c.client_id}
                onClick={() => setSelectedClient(c)}
                className={`w-full text-left p-4 rounded-lg border transition-all ${
                  selectedClient?.client_id === c.client_id
                    ? 'bg-teal-500/10 border-teal-500/40'
                    : 'bg-slate-800/60 border-slate-700 hover:bg-slate-800'
                }`}
                data-testid="client-card"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-teal-400 to-cyan-500 flex items-center justify-center text-white text-xs font-semibold shrink-0">
                      {(c.name || 'U').substring(0, 2).toUpperCase()}
                    </div>
                    <span className="text-white font-medium truncate">{c.name || 'Unknown Client'}</span>
                  </div>
                  {c.last_triage_level && (
                    <Badge variant="outline" className={`${triageColor(c.last_triage_level)} text-[10px] shrink-0`}>
                      {c.last_triage_level}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                  <span className="flex items-center gap-1"><Activity className="w-3 h-3" />{c.total_sessions} visit(s)</span>
                  {c.risk_flags?.length > 0 && (
                    <span className="flex items-center gap-1 text-red-300"><ShieldAlert className="w-3 h-3" />{c.risk_flags.length} flag(s)</span>
                  )}
                </div>
                {c.recurring_symptoms?.length > 0 && (
                  <p className="text-xs text-slate-500 mt-1 truncate">{c.recurring_symptoms.slice(0, 3).join(', ')}</p>
                )}
              </button>
            ))
          )}
        </div>

        {/* Detail */}
        <div className="lg:col-span-2">
          {!selectedClient ? (
            <Card className="bg-slate-800 border-slate-700 h-full">
              <CardContent className="flex flex-col items-center justify-center py-20 text-slate-400">
                <User className="w-12 h-12 mb-3 opacity-40" />
                <p>Select a client to view their full profile</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <CardTitle className="text-white flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-teal-400 to-cyan-500 flex items-center justify-center text-white font-semibold">
                      {(selectedClient.name || 'U').substring(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <div>{selectedClient.name || 'Unknown Client'}</div>
                      <div className="text-xs font-normal text-slate-400">{selectedClient.industry_type} · {selectedClient.total_sessions} visit(s)</div>
                    </div>
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedClient(null)} className="text-slate-400 lg:hidden">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-5">
                {/* Contact + key meta */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                  <Meta icon={Phone} label="Phone" value={selectedClient.phone || '—'} />
                  <Meta icon={Mail} label="Email" value={selectedClient.email || '—'} />
                  <Meta icon={Clock} label="Last seen" value={selectedClient.last_seen ? new Date(selectedClient.last_seen).toLocaleDateString() : '—'} />
                </div>

                {/* Narrative summary */}
                {selectedClient.profile_summary && (
                  <div className="p-4 rounded-lg bg-slate-900/50 border border-slate-700">
                    <div className="flex items-center gap-2 text-sm text-teal-300 mb-2"><FileText className="w-4 h-4" /> Profile Summary</div>
                    <p className="text-sm text-slate-200 leading-relaxed">{selectedClient.profile_summary}</p>
                  </div>
                )}

                {/* Chips */}
                <ChipRow icon={ShieldAlert} title="Risk Flags" items={selectedClient.risk_flags} color="bg-red-500/20 text-red-300 border-red-500/30" />
                <ChipRow icon={Stethoscope} title="Recurring Symptoms" items={selectedClient.recurring_symptoms} color="bg-amber-500/20 text-amber-300 border-amber-500/30" />
                <ChipRow icon={Activity} title="Conditions" items={selectedClient.conditions} color="bg-blue-500/20 text-blue-300 border-blue-500/30" />
                <ChipRow icon={Pill} title="Medications" items={selectedClient.medications} color="bg-purple-500/20 text-purple-300 border-purple-500/30" />
                <ChipRow icon={AlertCircle} title="Allergies" items={selectedClient.allergies} color="bg-rose-500/20 text-rose-300 border-rose-500/30" />

                {/* Visit history */}
                <div>
                  <div className="flex items-center gap-2 text-sm text-slate-300 mb-2"><Clock className="w-4 h-4" /> Visit History</div>
                  <div className="space-y-2">
                    {(selectedClient.visits || []).slice().reverse().map((v, i) => (
                      <div key={i} className="p-3 rounded-lg bg-slate-900/40 border border-slate-700">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="text-xs text-slate-400">{v.date ? new Date(v.date).toLocaleString() : 'Unknown date'}</span>
                          {v.triage_level && (
                            <Badge variant="outline" className={`${triageColor(v.triage_level)} text-[10px]`}>{v.triage_level}</Badge>
                          )}
                        </div>
                        {v.chief_complaint && <p className="text-sm text-white">{v.chief_complaint}</p>}
                        {v.summary && <p className="text-xs text-slate-400 mt-1">{v.summary}</p>}
                      </div>
                    ))}
                    {(!selectedClient.visits || selectedClient.visits.length === 0) && (
                      <p className="text-sm text-slate-500">No visits recorded.</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

const Meta = ({ icon: Icon, label, value }) => (
  <div className="p-3 rounded-lg bg-slate-900/40 border border-slate-700">
    <div className="flex items-center gap-1 text-xs text-slate-400 mb-1"><Icon className="w-3 h-3" />{label}</div>
    <div className="text-sm text-white truncate">{value}</div>
  </div>
);

const ChipRow = ({ icon: Icon, title, items, color }) => {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="flex items-center gap-2 text-sm text-slate-300 mb-2"><Icon className="w-4 h-4" /> {title}</div>
      <div className="flex flex-wrap gap-2">
        {items.map((it, i) => (
          <Badge key={i} variant="outline" className={`${color}`}>{it}</Badge>
        ))}
      </div>
    </div>
  );
};

export default ClientProfiles;
